import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { TextAreaField } from "@/components/common/text-field";
import ScrollModal from "@/components/common/scroll-modal";
import { Heading, Button, Icon } from "@elaniintc/goes-design-system";
import { DialogTitle } from "@/components/ui/dialog";

interface ConfirmUnArchiveModalProps {
  open: boolean;
  onClose: () => void;
  onDelete: (id: string, status: string, reason?: string) => void;
  id: string;
}

const unarchiveSchema = z.object({
  reason: z
    .string()
    .refine((val) => val.length <= 120, {
      error: "Máximo 120 caracteres",
    })
    .optional()
    .or(z.literal("")),
});

type UnarchiveFormData = z.infer<typeof unarchiveSchema>;

function ConfirmUnArchiveModal({
  open,
  onClose,
  onDelete,
  id,
}: ConfirmUnArchiveModalProps) {
  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    formState: { errors, isSubmitting, isValid },
    reset,
  } = useForm<UnarchiveFormData>({
    resolver: zodResolver(unarchiveSchema),
    mode: "onChange",
    defaultValues: {
      reason: "",
    },
  });

  const reasonValue = watch("reason");

  const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length <= 120) {
      setValue("reason", value);
      trigger("reason");
    }
  };

  const onSubmit = (data: UnarchiveFormData) => {
    onDelete(id, "UNACTIVE", data.reason);
    reset();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <ScrollModal
      isOpen={open}
      onClose={handleClose}
      srOnlyDescription={false}
      modalContentClassName="w-full max-w-[520px] max-h-[680px] h-full flex flex-col gap-0"
      header={
        <div className="flex items-center justify-between px-8 py-3">
          <DialogTitle>Desarchivar proceso</DialogTitle>
          <Button
            type="button"
            onClick={handleClose}
            variant="quiet"
            aria-label="Cerrar"
            className="w-8 h-8 cursor-pointer"
          >
            <Icon type="far" name="times" />
          </Button>
        </div>
      }
      footer={
        <div className="flex justify-end gap-2 py-3 px-8 rounded-b-lg">
          <Button
            type="button"
            variant="tertiary"
            className="w-[89px] h-[44px] font-semibold border"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            form="unarchive-process-form"
            className="w-[89px] h-[44px] font-semibold"
            disabled={isSubmitting || !isValid}
          >
            Continuar
          </Button>
        </div>
      }
    >
      <form
        id="unarchive-process-form"
        onSubmit={handleSubmit(onSubmit)}
        className="p-modal-md flex flex-col gap-4"
      >
        <div className="flex py-4 justify-center flex-col items-center">
          <Heading variant="h2" className="mb-1 text-center">
            ¿Quieres volver a publicar el proceso?
          </Heading>
          <p className="text-center text-body font-normal leading-3xl">
            Al desarchivar, el proceso aparecerá al usuario y se podrá recibir
            nuevos trámites.
          </p>
        </div>

        <div className="w-full flex justify-between mb-[-14px]">
          <label className="text-md text-body-loud font-medium">
            Motivo de la reactivación
          </label>
          <p className="text-sm text-body-quiet">Opcional</p>
        </div>
        <div className="flex flex-col">
          <Controller
            name="reason"
            control={control}
            render={({ field }) => (
              <TextAreaField
                label=""
                placeholder="Escribe el motivo de la reactivación..."
                maxLength={120}
                className="min-h-[88px]"
                value={field.value || ""}
                onChange={handleReasonChange}
                onBlur={field.onBlur}
                error={errors.reason?.message || ""}
              />
            )}
          />
          <p className="text-xs text-body-quiet text-right mt-1">
            Caracteres: {reasonValue?.length || 0}/120
          </p>
        </div>
      </form>
    </ScrollModal>
  );
}

export default ConfirmUnArchiveModal;
